import React from "react";
import DatatableComponent from "../../helpers/DatatableComponent";
import BasicMenu from "../../helpers/menu";
import FormDialog from "../../helpers/FormDialog";
import DeleteDialog from "../../helpers/DeleteDialog";
import { useHistory } from "react-router";

function LeaveTypeComponent(props) {
  const [open, setOpen] = React.useState(0);
  const [value, setValue] = React.useState({});
  const { handleDelete } = props;
  const [list, setList] = React.useState([]);
  const [id, setId] = React.useState();
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(null);
  const [count, setCount] = React.useState(0);

  // Predefined mapping of leave names to types
  const leaveTypeMapping = {
    "annual leave": "annual",
    "sick leave": "sick",
    "maternity leave": "maternity",
    "paternity leave": "paternity",
    "study leave/examination leave": "study",
  };

  const getLeaveType = (name, isAnnual) => {
    if (!name) return null;

    const lowerName = name.toLowerCase();

    if (leaveTypeMapping[lowerName]) {
      return leaveTypeMapping[lowerName];
    }

    // Ensure isAnnual is boolean
    if (isAnnual === true) {
      return "annual";
    }

    // For all other leaves, use the name itself as the type
    return lowerName;
  };

  const setValue0 = (name, val) => {
    if (!name) {
      return null;
    }

    // Convert isAnnual to boolean if needed
    let newVal = val;
    if (name === "isAnnual") {
      newVal = val === true || val === "true";
    }

    setValue((v) => {
      const newValue = { ...v, [name]: newVal };

      // Explicitly set the type field whenever name or isAnnual changes
      if (name === "name" || name === "isAnnual") {
        const leaveName = name === "name" ? newVal : v.name;
        const isAnnualBool =
          name === "isAnnual"
            ? newVal
            : v.isAnnual === true || v.isAnnual === "true";
        if (leaveName) {
          newValue.type = getLeaveType(leaveName, isAnnualBool);
        }
      }

      return newValue;
    });
  };

  const columns = [
    {
      name: "name",
      label: "Name",
    },
    {
      name: "isAnnual",
      label: "Annual",
      options: {
        customBodyRender: (v) => (v ? "Yes" : "No"),
      },
    },
    {
      name: "leaveDays",
      label: "Days",
    },
    {
      name: "id",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (
          <BasicMenu
            onEdit={() => {
              setOpen(2);
              const obj = list.find((vx) => vx.id === v);
              setId(v);

              // First set basic values
              setValue({
                name: obj?.name,
                leaveDays: obj?.leaveDays,
                isAnnual: obj?.isAnnual,
                message: obj?.message,
                // Explicitly include type from the object
                type: obj?.type,
              });

              // Then ensure type is correctly set based on name and isAnnual
              setTimeout(() => {
                setValue((prev) => ({
                  ...prev,
                  type: getLeaveType(prev.name, prev.isAnnual),
                }));
              }, 0);
            }}
            onDelete={() => {
              setDeleteId(v);
              setDeleteOpen(true);
            }}
          />
        ),
      },
    },
  ];

  const onSuccess = () => setCount((v) => v + 1);

  // Prepare the payload ensuring type is included
  const preparePayload = () => {
    const typeValue = getLeaveType(value.name, value.isAnnual);

    return {
      ...value,
      isAnnual: value.isAnnual || false,
      type: typeValue, // Explicitly set type
    };
  };

  return (
    <div className="w-full mb-12 px-4">
      <DeleteDialog
        open={deleteOpen}
        setOpen={() => setDeleteOpen(false)}
        onSuccess={onSuccess}
        url={
          deleteId
            ? `/api/request/permission-category/delete/${deleteId}`
            : null
        }
        title="Delete Leave Type"
        message="Are you sure you want to delete this leave type? This action cannot be undone."
      />
      <FormDialog
        open={open > 0}
        onSuccess={onSuccess}
        setOpen={setOpen}
        url={
          open === 1
            ? "/api/request/permission-category/create"
            : `/api/request/permission-category/update/${id}`
        }
        title={open === 1 ? "Add New Type" : "Edit Leave Type"}
        fields={[
          {
            name: "name",
            label: "Name",
            value: value["name"],
            required: true,
            type: "select",
            values: [
              { label: "Annual Leave", value: "Annual leave" },
              { label: "Sick Leave", value: "sick leave" },
              { label: "Maternity Leave", value: "maternity leave" },
              {
                label: "Study Leave/Examination Leave",
                value: "study leave/examination leave",
              },
              {
                label: "Bereavement of a Brother-in-Law or Sister-in-Law",
                value: "bereavement of a brother-in-law or sister-in-law",
              },
              {
                label: "Worker's Wedding Leave",
                value: "worker's wedding leave",
              },
              { label: "Paternity Leave", value: "Paternity Leave" },
              {
                label: "Bereavement of a Grandparent",
                value: "bereavement of a grandparent",
              },
              {
                label: "Bereavement of a Brother or Sister",
                value: "bereavement of a brother or sister",
              },
              {
                label: "Bereavement of a Spouse",
                value: "bereavement of a spouse",
              },
              {
                label: "Bereavement of a Child",
                value: "bereavement of a child",
              },
              { label: "Bereavement of a Parent", value: "bereavement of a parent" },
              { label: "Permission", value: "permission" },
              {
                label: "Bereavement of a Father-in-Law or Mother-in-Law",
                value: "bereavement of a father-in-law or mother-in-law",
              },
            ],
            setValue: (val) => setValue0("name", val),
          },
          {
            name: "isAnnual",
            label: "Annually?",
            value: value["isAnnual"],
            type: "radio",
            col: 12,
            checkCol: 4,
            values: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
            setValue: (val) => setValue0("isAnnual", val),
          },
          {
            name: "leaveDays",
            label: "Leave Days",
            value: value["leaveDays"],
            required: false,
            type: "number",
            setValue: (val) => setValue0("leaveDays", val),
          },
          {
            name: "message",
            label: "Description",
            value: value["message"],
            type: "textarea",
            setValue: (val) => setValue0("message", val),
          },
          // Hidden field to ensure type is in the form values
          {
            name: "type",
            value: value["type"],
            required: false,
            type: "hidden",
            setValue: (val) => setValue0("type", val),
          },
        ].filter(Boolean)}
        payload={preparePayload()}
      />
      <DatatableComponent
        onAddNew={() => {
          setOpen(1);
          setValue({}); // Reset form values
        }}
        search={false}
        viewColumns={false}
        searchfield={true}
        processData={(data) => {
          setList(data.data);
          return data;
        }}
        elevation={1}
        columns={columns}
        url={`/api/request/categories/datatable?count=${count}`}
        title={"Leave Types List"}
      />
    </div>
  );
}

export default LeaveTypeComponent;
